import { useEffect, useState } from 'react';

/**
 * A search term, debounced and trimmed.
 *
 * Trimming matters because every server-side search is a substring match
 * (`contains`), so a stray trailing space makes `"gravel "` fail to find
 * `"Gravel"` — usually after pasting, where the space is invisible. Only the
 * outer whitespace goes: spaces *inside* the term are left alone so multi-word
 * searches still work while they are being typed.
 *
 * The trimming happens here rather than on the input's value so the box still
 * shows exactly what was typed; it is only the term sent to the API that is
 * normalised. A useful side effect is that adding or removing a trailing space
 * leaves the debounced value unchanged, so it no longer triggers a refetch.
 *
 * Replaces seven near-identical local copies of this hook, all of which were
 * used for search and none of which trimmed.
 */
export function useDebouncedSearch(value: string, ms = 350): string {
  const [debounced, setDebounced] = useState(() => value.trim());

  useEffect(() => {
    const trimmed = value.trim();
    const timer = setTimeout(() => setDebounced(trimmed), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);

  return debounced;
}

export default useDebouncedSearch;
